import { applicationApi } from '../../common/api';

export interface LotteryPool {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LotteryCategory {
  id: number;
  poolId: number;
  categoryId: string;
  name: string;
  maxTickets: number;
  issuedTickets?: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LotteryTicket {
  id: number;
  memberId: string;
  poolId: number;
  categoryId: number;
  ticketNumber: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  pool?: {
    id: number;
    name: string;
  };
  category?: {
    id: number;
    categoryId?: string;
    name: string;
  };
  member?: {
    id: string;
    name: string;
  };
}

export interface MembersLotterySummaryResponse {
  poolId?: number;
  poolName?: string;
  categories: { id: number; categoryId: string; name: string }[];
  members: {
    memberId: string;
    memberName: string;
    totalsByCategory: { categoryId: number; categoryCode: string; count: number; ticketNumbers: string[] }[];
    totalTickets: number;
  }[];
}

export interface PaginatedLotteryTickets {
  data: LotteryTicket[];
  total: number;
}

export const lotteryApi = applicationApi.injectEndpoints({
  endpoints: (builder) => ({
    // Pool endpoints
    getAllPools: builder.query<LotteryPool[], void>({
      query: () => 'lottery/pools',
      providesTags: [{ type: 'LotteryPool', id: 'LIST' }],
    }),
    getPool: builder.query<LotteryPool, number>({
      query: (id) => `lottery/pools/${id}`,
      providesTags: [{ type: 'LotteryPool', id: 'LIST' }],
    }),
    createPool: builder.mutation<LotteryPool, Partial<LotteryPool>>({
      query: (body) => ({
        url: 'lottery/pools',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LotteryPool', id: 'LIST' }],
    }),
    updatePool: builder.mutation<LotteryPool, any>({
      query: (body) => ({
        url: `lottery/pools/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'LotteryPool', id: 'LIST' }],
    }),
    deletePool: builder.mutation<void, number>({
      query: (id) => ({
        url: `lottery/pools/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'LotteryPool', id: 'LIST' }],
    }),

    // Category endpoints
    getCategoriesByPool: builder.query<LotteryCategory[], number>({
      query: (poolId) => `lottery/categories/pool/${poolId}`,
      providesTags: [{ type: 'LotteryCategory', id: 'LIST' }],
    }),
    getCategory: builder.query<LotteryCategory, number>({
      query: (id) => `lottery/categories/${id}`,
      providesTags: [{ type: 'LotteryCategory', id: 'LIST' }],
    }),
    createCategory: builder.mutation<LotteryCategory, Partial<LotteryCategory>>({
      query: (body) => ({
        url: 'lottery/categories',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LotteryCategory', id: 'LIST' }],
    }),
    updateCategory: builder.mutation<LotteryCategory, any>({
      query: (body) => ({
        url: `lottery/categories/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'LotteryCategory', id: 'LIST' }],
    }),
    deleteCategory: builder.mutation<void, number>({
      query: (id) => ({
        url: `lottery/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'LotteryCategory', id: 'LIST' }],
    }),

    // Ticket endpoints
    assignTicket: builder.mutation<LotteryTicket, any>({
      query: (body) => ({
        url: 'lottery/tickets/assign',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LotteryTicket', id: 'LIST' }],
    }),
    getAllTickets: builder.query<PaginatedLotteryTickets, { page: number; pageSize: number }>({
      query: ({ page, pageSize }) => `lottery/tickets?page=${page}&pageSize=${pageSize}`,
      providesTags: [{ type: 'LotteryTicket', id: 'LIST' }],
    }),
    deleteTicket: builder.mutation<void, number>({
      query: (id) => ({
        url: `lottery/tickets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'LotteryTicket', id: 'LIST' }],
    }),

    // Reports
    getMembersLotterySummary: builder.query<MembersLotterySummaryResponse, number | void>({
      query: (poolId) => (poolId ? `lottery/report/members?poolId=${poolId}` : 'lottery/report/members'),
    }),
  }),
});

export const {
  useGetAllPoolsQuery,
  useGetPoolQuery,
  useCreatePoolMutation,
  useUpdatePoolMutation,
  useDeletePoolMutation,
  useGetCategoriesByPoolQuery,
  useGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useAssignTicketMutation,
  useGetAllTicketsQuery,
  useDeleteTicketMutation,
  useGetMembersLotterySummaryQuery,
} = lotteryApi;
